import { useEffect, useCallback } from 'react';
import { store } from '../stores/store';

/**
 * Hook to register a cleanup handler that runs when session ends (timeout or manual cancel).
 * The cleanup handler is automatically unregistered when the component unmounts.
 *
 * @param key - Unique identifier for this cleanup handler (e.g., 'payment-cash')
 * @param cleanup - Function to execute during session end
 *
 * @example
 * ```tsx
 * useSessionCleanup('payment-cash', () => {
 *   currencyDevice?.endTransaction();
 *   setAmountEntered(0);
 * });
 * ```
 */
export function useSessionCleanup(key: string, cleanup: () => void) {
	const stableCleanup = useCallback(cleanup, [cleanup]);

	useEffect(() => {
		store.sessionStore.registerCleanup(key, stableCleanup);

		return () => {
			store.sessionStore.unregisterCleanup(key);
		};
	}, [key, stableCleanup]);
}
